import type {
  AdvisorMovement,
  AppData,
  Distributor,
  FundKey,
  FundMovement,
  ProfitDistribution,
  Receivable,
  Sale,
} from "@/types";

// Precio costo asesor = precio público menos el margen del asesor.
export const costAdvisor = (pricePublic: number, margin: number): number =>
  Math.round(pricePublic * (1 - margin / 100));

// Base de costo reposición con la que se mide la ganancia del canal asesor:
// el 75% del precio público (el margen comercial total es del 25%).
export const REPOSITION_COST_PCT = 75;
export const costBaseReposicion = (pricePublic: number): number =>
  Math.round(pricePublic * (REPOSITION_COST_PCT / 100));

export interface AdvisorAccount {
  advisor: Distributor;
  margin: number;
  consignedUnits: number; // unidades en consignación (propiedad del admin)
  consignedValue: number; // valor a costo asesor de lo consignado
  ownedUnits: number; // unidades ya compradas por el asesor
  compradoUnits: number; // total histórico comprado
  compradoValue: number; // total histórico comprado ($)
  soldConsignedUnits: number; // unidades de consignación vendidas a clientes
  charges: number; // total cargado a su cuenta (compras + ventas + fees)
  paid: number; // total abonado / liquidado
  debt: number; // saldo que debe al admin
  movements: AdvisorMovement[];
}

// Cuenta corriente de cada asesor (consignado vs comprado vs debe).
export const buildAdvisorAccounts = (data: AppData): AdvisorAccount[] =>
  data.distributors.map((advisor) => {
    const margin = advisor.margin;
    let consignedUnits = 0;
    let consignedValue = 0;
    let ownedUnits = 0;

    data.inventory.forEach((inv) => {
      const consigned = inv.stockByAdvisor[advisor.id] ?? 0;
      const owned = inv.stockOwnedByAdvisor?.[advisor.id] ?? 0;
      if (consigned <= 0 && owned <= 0) return;
      const product = data.products.find((p) => p.id === inv.productId);
      const cost = product ? costAdvisor(product.pricePublic, margin) : 0;
      consignedUnits += consigned;
      consignedValue += consigned * cost;
      ownedUnits += owned;
    });

    const movements = data.advisorMovements.filter((m) => m.advisorId === advisor.id);
    const compradoUnits = movements
      .filter((m) => m.kind === "compra")
      .reduce((a, m) => a + m.qty, 0);
    const compradoValue = movements
      .filter((m) => m.kind === "compra")
      .reduce((a, m) => a + m.amount, 0);
    const soldConsignedUnits = movements
      .filter((m) => m.kind === "venta")
      .reduce((a, m) => a + m.qty, 0);
    const charges = movements
      .filter((m) => m.kind === "compra" || m.kind === "venta" || m.kind === "fee")
      .reduce((a, m) => a + m.amount, 0);
    const paid = movements
      .filter((m) => m.kind === "pago")
      .reduce((a, m) => a + m.amount, 0);

    return {
      advisor,
      margin,
      consignedUnits,
      consignedValue,
      ownedUnits,
      compradoUnits,
      compradoValue,
      soldConsignedUnits,
      charges,
      paid,
      debt: Math.max(0, charges - paid),
      movements,
    };
  });

export interface AccountingSummary {
  capitalCost: number; // dinero invertido en inventario propio (a costo base)
  inventoryPublicValue: number; // inventario propio a precio público
  consignedToAdvisors: number; // valor consignado a asesores (a costo asesor)
  advisorDebt: number; // total por cobrar a asesores
  directSales: number; // ventas directas del admin
  advisorPurchases: number; // ventas a asesores (compras)
  consignmentPaid: number; // consignaciones liquidadas
  recognizedIncome: number; // ingresos reconocidos (caja)
  realizedProfit: number; // ganancia estimada
  clientReceivable: number; // cartera pendiente de clientes
  expensesTotal: number; // gastos + caja menor registrados
  projectedProfit: number; // ganancia proyectada sobre inventario propio
  projectionDirect: number; // proyección si se vende en punto / internet
  projectionConsigned: number; // proyección si se vende al asesor
}

export const buildAccounting = (data: AppData): AccountingSummary => {
  const costOf = (productId: string): number =>
    data.products.find((p) => p.id === productId)?.costBase ?? 0;
  // Ganancia del canal asesor medida sobre la base reposición (75% del PVP),
  // no sobre el costo real de compra; así una venta al 25% da 0 y al 20% da +5%.
  const repoCostOf = (productId: string): number =>
    costBaseReposicion(data.products.find((p) => p.id === productId)?.pricePublic ?? 0);

  let capitalCost = 0;
  let inventoryPublicValue = 0;
  let consignedToAdvisors = 0;
  let projectionDirect = 0;
  let projectionConsigned = 0;

  data.inventory.forEach((inv) => {
    const product = data.products.find((p) => p.id === inv.productId);
    const baseCost = product?.costBase ?? 0;
    const publicPrice = product?.pricePublic ?? 0;

    // Inventario propio del admin: casa + punto clear.
    const own = inv.stockCasa + inv.stockPuntoClear;
    capitalCost += own * baseCost;
    inventoryPublicValue += own * publicPrice;
    projectionDirect += own * (publicPrice - baseCost);

    // Inventario consignado a asesores (admin lo recupera a costo asesor al venderlo).
    data.distributors.forEach((advisor) => {
      const consigned = inv.stockByAdvisor[advisor.id] ?? 0;
      if (consigned <= 0) return;
      const unit = costAdvisor(publicPrice, advisor.margin);
      consignedToAdvisors += consigned * unit;
      projectionConsigned += consigned * (unit - costBaseReposicion(publicPrice));
    });
  });

  const accounts = buildAdvisorAccounts(data);
  const advisorDebt = accounts.reduce((a, x) => a + x.debt, 0);

  const directSalesList = data.sales.filter((s) => s.advisorId === "admin" && !s.historical);
  const directSales = directSalesList.reduce((a, s) => a + s.total, 0);
  const directProfit = directSalesList.reduce(
    (a, s) => a + (s.total - s.qty * costOf(s.productId)),
    0
  );

  const purchaseMoves = data.advisorMovements.filter((m) => m.kind === "compra");
  const advisorPurchases = purchaseMoves.reduce((a, m) => a + m.amount, 0);
  const purchaseProfit = purchaseMoves.reduce(
    (a, m) => a + (m.amount - m.qty * repoCostOf(m.productId)),
    0
  );

  const saleMoves = data.advisorMovements.filter((m) => m.kind === "venta");
  const consignmentProfit = saleMoves.reduce(
    (a, m) => a + (m.amount - m.qty * repoCostOf(m.productId)),
    0
  );

  const consignmentPaid = data.advisorMovements
    .filter((m) => m.kind === "pago")
    .reduce((a, m) => a + m.amount, 0);

  const clientReceivable = data.receivables.reduce(
    (a, r) => a + receivableBalance(r),
    0
  );

  const expensesTotal = data.expenses.reduce((a, e) => a + e.amount, 0);

  const recognizedIncome = directSales + advisorPurchases + consignmentPaid;
  const realizedProfit = directProfit + purchaseProfit + consignmentProfit;

  return {
    capitalCost,
    inventoryPublicValue,
    consignedToAdvisors,
    advisorDebt,
    directSales,
    advisorPurchases,
    consignmentPaid,
    recognizedIncome,
    realizedProfit,
    clientReceivable,
    expensesTotal,
    projectedProfit: projectionDirect + projectionConsigned,
    projectionDirect,
    projectionConsigned,
  };
};

// ---- Comparativo mes a mes de la ganancia realizada por canal ----

export interface MonthlyChannelProfit {
  key: string; // formato yyyy-mm
  label: string; // etiqueta corta, ej. "may 2026"
  direct: number; // ganancia de ventas directas (punto / internet)
  advisor: number; // ganancia de la red (compras del asesor + consignación vendida)
  total: number;
}

const SHORT_MONTHS = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];

const toMonthKey = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabelFromKey = (key: string): string => {
  const [year, month] = key.split("-");
  const idx = Number(month) - 1;
  const name = SHORT_MONTHS[idx] ?? month;
  return `${name} ${year}`;
};

// Ganancia realizada agrupada por mes y separando venta directa de la red de asesores.
export const buildMonthlyProfit = (data: AppData): MonthlyChannelProfit[] => {
  const costOf = (productId: string): number =>
    data.products.find((p) => p.id === productId)?.costBase ?? 0;
  const repoCostOf = (productId: string): number =>
    costBaseReposicion(data.products.find((p) => p.id === productId)?.pricePublic ?? 0);

  const map = new Map<string, MonthlyChannelProfit>();
  const ensure = (key: string): MonthlyChannelProfit => {
    const found = map.get(key);
    if (found) return found;
    const created: MonthlyChannelProfit = {
      key,
      label: monthLabelFromKey(key),
      direct: 0,
      advisor: 0,
      total: 0,
    };
    map.set(key, created);
    return created;
  };

  // Ventas directas del admin (punto / internet / casa).
  data.sales.forEach((s) => {
    if (s.advisorId !== "admin" || s.historical) return;
    const key = toMonthKey(s.date);
    if (!key) return;
    ensure(key).direct += s.total - s.qty * costOf(s.productId);
  });

  // Red: compras del asesor + unidades de consignación vendidas a clientes.
  data.advisorMovements.forEach((m) => {
    if (m.kind !== "compra" && m.kind !== "venta") return;
    const key = toMonthKey(m.date);
    if (!key) return;
    ensure(key).advisor += m.amount - m.qty * repoCostOf(m.productId);
  });

  const list = Array.from(map.values());
  list.forEach((r) => {
    r.total = r.direct + r.advisor;
  });
  list.sort((a, b) => a.key.localeCompare(b.key));
  return list;
};

// ---- Mercancía recibida por el asesor (consignación y compras), por producto ----

export interface ReceivedGoodsRow {
  productName: string;
  consignedUnits: number; // unidades que llegaron en consignación
  purchasedUnits: number; // unidades que el asesor compró (pagadas)
  consignedValue: number; // valor (costo asesor) de lo consignado
  purchasedValue: number; // valor (costo asesor) de lo comprado
  totalUnits: number;
  totalValue: number;
  fromExcel: boolean; // si el ingreso vino del Excel AMWAY
}

// Agrupa la mercancía que le llegó al asesor (consignación o compra) por producto.
// Se actualiza cada vez que recibe mercancía, sea pagada o en consignación.
export const buildReceivedGoods = (movements: AdvisorMovement[]): ReceivedGoodsRow[] => {
  const map = new Map<string, ReceivedGoodsRow>();
  movements
    .filter((m) => m.kind === "consignacion" || m.kind === "compra")
    .forEach((m) => {
      const key = (m.productName || m.productId).trim().toLowerCase();
      let row = map.get(key);
      if (!row) {
        row = {
          productName: m.productName || "Producto",
          consignedUnits: 0,
          purchasedUnits: 0,
          consignedValue: 0,
          purchasedValue: 0,
          totalUnits: 0,
          totalValue: 0,
          fromExcel: false,
        };
        map.set(key, row);
      }
      if (m.kind === "consignacion") {
        row.consignedUnits += m.qty;
        row.consignedValue += m.amount;
      } else {
        row.purchasedUnits += m.qty;
        row.purchasedValue += m.amount;
      }
      if (/excel/i.test(m.note || "")) row.fromExcel = true;
    });
  const list = Array.from(map.values());
  list.forEach((r) => {
    r.totalUnits = r.consignedUnits + r.purchasedUnits;
    r.totalValue = r.consignedValue + r.purchasedValue;
  });
  list.sort((a, b) => b.totalValue - a.totalValue);
  return list;
};

// ---- Fondos contables (reparto del 25% de utilidad por venta) ----

export interface FundMeta {
  key: FundKey;
  label: string;
  short: string;
  icon: string;
  distKey: keyof ProfitDistribution;
}

// Orden y nombres de los cuatro fondos. Cada uno toma su % de la distribución
// configurada: Caja Menor = reinversión (13%), Asesor = advisor (5%),
// Gerencia = management (5%) y Donación = donation (2%).
export const FUND_META: FundMeta[] = [
  { key: "caja", label: "Fondo Caja Menor", short: "Caja Menor", icon: "ri-safe-2-line", distKey: "reinvestment" },
  { key: "asesor", label: "Fondo Asesor / Ayudante de Punto", short: "Asesor / Ayudante", icon: "ri-user-star-line", distKey: "advisor" },
  { key: "gerencia", label: "Fondo Gerencia", short: "Gerencia", icon: "ri-briefcase-4-line", distKey: "management" },
  { key: "donacion", label: "Fondo Donación", short: "Donación", icon: "ri-heart-3-line", distKey: "donation" },
];

export interface FundSummary {
  key: FundKey;
  label: string;
  short: string;
  icon: string;
  pct: number; // % configurado
  income: number; // acumulado por ventas
  spent: number; // pagos de compras/gastos con este fondo
  transfersIn: number;
  transfersOut: number;
  loanIn: number; // inyecciones de préstamo de Juank (solo caja)
  loanRepaid: number; // devoluciones de préstamo hechas con este fondo
  balance: number;
}

export interface FundsSummary {
  funds: FundSummary[];
  totalIncome: number;
  totalBalance: number;
  totalSpent: number;
  totalSalesCatalog: number; // valor total de catálogo vendido (base de los %)
  juankLent: number; // total prestado por Juank.unlimited
  juankReturned: number; // total devuelto a Juank
  juankBalance: number; // pendiente por devolver a Juank
}

const emptyFund = (): Record<FundKey, number> => ({
  caja: 0,
  asesor: 0,
  gerencia: 0,
  donacion: 0,
});

const fundKeyOf = (value: unknown): FundKey | null => {
  const s = String(value ?? "");
  return s === "caja" || s === "asesor" || s === "gerencia" || s === "donacion" ? s : null;
};

// Calcula los saldos vivos de los cuatro fondos a partir de las ventas, los
// pagos hechos con cada fondo, las transferencias manuales y los préstamos.
export const buildFunds = (data: AppData): FundsSummary => {
  const dist = data.settings.distribution;
  const movements: FundMovement[] = data.settings.fundMovements ?? [];

  // Base informativa: valor total de catálogo vendido (todas las ventas).
  const totalSalesCatalog = data.sales.filter((s) => !s.historical).reduce((a, s) => a + s.total, 0);

  // Margen comercial total que se reparte (suma de los cuatro fondos = 25%).
  const marginTotal =
    (dist.donation ?? 0) +
    (dist.advisor ?? 0) +
    (dist.reinvestment ?? 0) +
    (dist.management ?? 0);

  // Reparto del margen por venta, según el canal y el descuento del asesor:
  //  · Venta DIRECTA (admin): reparte el margen completo (25%) entre los 4 fondos.
  //  · Venta de ASESOR: el asesor se queda con su descuento. Al admin solo le
  //    queda el margen diferencial (margenTotal − descuento del asesor), que se
  //    acredita ÍNTEGRO al Fondo Gerencia. Si el asesor está al 25% (o más) no
  //    queda margen y no se alimenta ningún fondo.
  const income = emptyFund();
  data.sales.forEach((s) => {
    if (s.historical) return;
    const total = s.total || 0;
    if (s.advisorId === "admin") {
      income.caja += total * ((dist.reinvestment ?? 0) / 100);
      income.asesor += total * ((dist.advisor ?? 0) / 100);
      income.gerencia += total * ((dist.management ?? 0) / 100);
      income.donacion += total * ((dist.donation ?? 0) / 100);
    } else {
      // Venta de la mercancía PROPIA del asesor: esa mercancía ya era suya, no es
      // del admin, así que no aporta nada a los fondos (ni margen diferencial).
      if (s.source === "propio") return;
      const differential = Math.max(0, marginTotal - (s.marginPct ?? 0));
      income.gerencia += total * (differential / 100);
    }
  });
  FUND_META.forEach((m) => {
    income[m.key] = Math.round(income[m.key]);
  });

  // Pagos de compras / gastos cargados a cada fondo (monto realmente pagado).
  const spent = emptyFund();
  data.expenses.forEach((e) => {
    const key = fundKeyOf(e.fund);
    if (!key) return;
    spent[key] += e.paidAmount ?? 0;
  });

  const transfersIn = emptyFund();
  const transfersOut = emptyFund();
  const loanIn = emptyFund();
  const loanRepaid = emptyFund();
  movements.forEach((m) => {
    if (m.kind === "transferencia") {
      const from = fundKeyOf(m.fromFund);
      const to = fundKeyOf(m.toFund);
      if (from) transfersOut[from] += m.amount;
      if (to) transfersIn[to] += m.amount;
    } else if (m.kind === "prestamo_juank") {
      const to = fundKeyOf(m.toFund);
      if (to) loanIn[to] += m.amount;
    } else if (m.kind === "pago_prestamo") {
      const from = fundKeyOf(m.fromFund);
      if (from) loanRepaid[from] += m.amount;
    }
  });

  const funds: FundSummary[] = FUND_META.map((m) => ({
    key: m.key,
    label: m.label,
    short: m.short,
    icon: m.icon,
    pct: dist[m.distKey] ?? 0,
    income: income[m.key],
    spent: spent[m.key],
    transfersIn: transfersIn[m.key],
    transfersOut: transfersOut[m.key],
    loanIn: loanIn[m.key],
    loanRepaid: loanRepaid[m.key],
    balance:
      income[m.key] +
      transfersIn[m.key] +
      loanIn[m.key] -
      transfersOut[m.key] -
      loanRepaid[m.key] -
      spent[m.key],
  }));

  const juankLent = data.loans
    .filter((l) => l.type === "Préstamo")
    .reduce((a, l) => a + l.value, 0);
  const juankReturned = data.loans
    .filter((l) => l.type === "Devolución")
    .reduce((a, l) => a + l.value, 0);

  return {
    funds,
    totalIncome: funds.reduce((a, f) => a + f.income, 0),
    totalBalance: funds.reduce((a, f) => a + f.balance, 0),
    totalSpent: funds.reduce((a, f) => a + f.spent, 0),
    totalSalesCatalog,
    juankLent,
    juankReturned,
    juankBalance: juankLent - juankReturned,
  };
};

// ---- Cartera de clientes: totales y emparejamiento con el directorio ----

// Total abonado de una cuenta por cobrar (suma de todos sus abonos).
export const receivablePaid = (r: Receivable): number =>
  r.payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);

// Saldo pendiente de una cuenta por cobrar (valor inicial − abonos).
export const receivableBalance = (r: Receivable): number =>
  Math.max(0, r.initialValue - receivablePaid(r));

// Normaliza un nombre (sin acentos ni mayúsculas) para emparejar cliente ↔ cartera.
export const normalizeClientName = (v: string): string =>
  v
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

// ¿La cuenta por cobrar corresponde a este cliente? Coincidencia exacta o por
// nombre contenido (ej. "NUBIA" ↔ "NUBIA AVENDAÑO"), exigiendo 4+ letras para
// evitar falsos positivos.
export const receivableMatchesClient = (
  receivableName: string,
  clientName: string
): boolean => {
  const a = normalizeClientName(receivableName);
  const b = normalizeClientName(clientName);
  if (!a || !b) return false;
  if (a === b) return true;
  const shorter = a.length <= b.length ? a : b;
  const longer = a.length <= b.length ? b : a;
  return shorter.length >= 4 && longer.includes(shorter);
};

// ---- Productos más comprados por un cliente (para saber qué ofrecerle) ----

export interface ClientTopProduct {
  productName: string;
  purchases: number; // número de veces que lo compró
  qty: number; // unidades totales compradas
  total: number; // total facturado
  lastDate: string; // última vez que lo compró
}

// Agrupa las compras de un cliente por producto y las ordena de mayor a menor
// (por unidades y luego por monto). Solo recibe las ventas de ese cliente.
export const buildClientTopProducts = (sales: Sale[]): ClientTopProduct[] => {
  const map = new Map<string, ClientTopProduct>();
  sales.forEach((s) => {
    const key = (s.productName || "").trim().toLowerCase();
    if (!key) return;
    let row = map.get(key);
    if (!row) {
      row = { productName: s.productName, purchases: 0, qty: 0, total: 0, lastDate: s.date };
      map.set(key, row);
    }
    row.purchases += 1;
    row.qty += s.qty;
    row.total += s.total;
    if (new Date(s.date).getTime() > new Date(row.lastDate).getTime()) row.lastDate = s.date;
  });
  const list = Array.from(map.values());
  list.sort((a, b) => b.qty - a.qty || b.total - a.total);
  return list;
};

// ---- Avisos de desfase de precio (la referencia es el precio de catálogo 100%) ----

export interface PriceAlert {
  key: string;
  productId: string;
  productName: string;
  kind: "costo" | "desfase";
  advisorId?: string;
  advisorName?: string;
  expected: number;
  recorded?: number;
  message: string;
}

// Detecta dos tipos de desfase, con el precio de catálogo como referencia:
//  1) Errores de carga: costo base ≥ precio público (el margen no cuadra).
//  2) Cuenta del asesor: un movimiento de tenencia / compra / venta guardó un
//     valor que no corresponde a "precio de catálogo − su % (20/25)".
export const buildPriceAlerts = (data: AppData): PriceAlert[] => {
  const alerts: PriceAlert[] = [];
  const productById = new Map(data.products.map((p) => [p.id, p]));

  data.products.forEach((p) => {
    if (p.pricePublic > 0 && p.costBase >= p.pricePublic) {
      alerts.push({
        key: `costo:${p.id}`,
        productId: p.id,
        productName: p.name,
        kind: "costo",
        expected: Math.round(p.pricePublic * (REPOSITION_COST_PCT / 100)),
        recorded: p.costBase,
        message: `Costo cargado ${p.costBase} ≥ precio público ${p.pricePublic}. Revisa este producto (costo sugerido ${Math.round(p.pricePublic * (REPOSITION_COST_PCT / 100))}).`,
      });
    }
  });

  data.distributors.forEach((adv) => {
    const margin = adv.margin;
    data.advisorMovements
      .filter(
        (m) =>
          m.advisorId === adv.id &&
          (m.kind === "consignacion" || m.kind === "compra" || m.kind === "venta")
      )
      .forEach((m) => {
        const product = productById.get(m.productId);
        if (!product || product.pricePublic <= 0) return;
        const expected = costAdvisor(product.pricePublic, margin);
        if (Math.round(m.unitValue) !== expected) {
          alerts.push({
            key: `desfase:${adv.id}:${m.productId}:${m.id}`,
            productId: m.productId,
            productName: m.productName || product.name,
            advisorId: adv.id,
            advisorName: `${adv.names} ${adv.surnames}`.trim(),
            kind: "desfase",
            expected,
            recorded: Math.round(m.unitValue),
            message: `${`${adv.names} ${adv.surnames}`.trim()}: ${m.productName || product.name} registró ${Math.round(m.unitValue)} pero debería ser ${expected} (catálogo ${product.pricePublic} − ${margin}%).`,
          });
        }
      });
  });

  return alerts;
};